import { Component, OnInit, ViewChild } from '@angular/core';
import { DateTime } from 'luxon';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { ErrorLibService } from 'src/shared/services/error-lib.service';
import { ReportChartService } from 'src/shared/services/report-chart.service';
import { ReportService } from 'src/shared/services/report.service';
import Swal from 'sweetalert2';
import { Installation } from '../../report/models/installation.model';
import { ReportChartData } from '../models/chartData.model';
import { Unsubscriber } from '../../unsubscriber/unsubscriber.component';
import { tzConvertISOEndDate, tzConvertISOStartDate, tzConvertUTC2Local } from 'src/assets/convertTimeZone/convertTimeZone';
import jsPDF from 'jspdf';

// Imports Standalone
import { PageHeaderComponent } from '../../page-header/page-header.component';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { DialogModule } from 'primeng/dialog';
import { FileUploadModule } from 'primeng/fileupload';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TranslateModule } from '@ngx-translate/core';
import { MatSelectModule } from '@angular/material/select';
import { MatInputModule } from '@angular/material/input';
import { DatePickerModule } from 'primeng/datepicker';

@Component({
  selector: 'app-report-charts',
  templateUrl: './report-charts.component.html',
  styleUrls: ['./report-charts.component.less'],
  imports: [
    CommonModule,
    PageHeaderComponent,
    ReactiveFormsModule,
    DatePickerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    DialogModule,
    FormsModule,
    TableModule,
    ButtonModule,
    CardModule,
    FileUploadModule,
    TagModule,
    TranslateModule,
  ],
})
export class ReportChartsComponent extends Unsubscriber implements OnInit {
  //Variáveis usadas para o paginator e para o sort.
  private paginator: MatPaginator;

  @ViewChild(MatPaginator) set matPaginator(mp: MatPaginator) {
    this.paginator = mp;
    this.setDataSourceAttributes();
  }

  /**
   * Função que configura o paginator.
   */
  setDataSourceAttributes() {
    this.dataSource.paginator = this.paginator;
  }

  public company: string | null = '';

  constructor(
    private reportService: ReportService,
    private reportChartService: ReportChartService,
    private errorService: ErrorLibService,
    private formbuilder: FormBuilder,
  ) {
    super();
    this.company = localStorage.getItem('lastCompanySelected');
  }

  ngOnInit(): void { }

  //Form control variables
  installationReferenceForm: FormControl = new FormControl(
    '',
    Validators.required,
  );
  selectedInstallationForm: FormControl = new FormControl(
    '',
    Validators.required,
  );
  selectedCounterForm: FormControl = new FormControl(1, Validators.required);

  //General variables
  selectedInstallations: Installation[] = [];
  filteredInstallations: Installation[] = [];
  eqTypeWithVersion: string | null;

  /** Opções de contador disponíveis para equipamentos de pulso **/
  counterOptions: number[] = [1, 2];

  chartData: ReportChartData[] = [
    new ReportChartData(
      '1',
      '2021-01-01 00:00:00',
      '1',
      '1',
      '1',
      '1',
      '1',
      '1',
      '1',
      '1',
    ),
    new ReportChartData(
      '2',
      '2021-01-01 00:00:00',
      '2',
      '1',
      '1',
      '2',
      '2',
      '2',
      '2',
      '2',
    ),
    new ReportChartData(
      '3',
      '2021-01-01 00:00:00',
      '3',
      '1',
      '1',
      '3',
      '3',
      '3',
      '3',
      '3',
    ),
  ];

  dataSource = new MatTableDataSource<ReportChartData>(this.chartData);

  public chartVariable: any;

  //flags
  step1IsConcluded: boolean = false;
  step2IsConcluded: boolean = false;
  step3IsConcluded: boolean = false;
  isLoadingInstallationSearch: boolean = false;
  isThereInstallationListed: boolean = false;
  noInstallationWarning: boolean = false;
  createChartBox: boolean = false;
  /** Indica se a instalação selecionada é de um equipamento de pulso **/
  isPulseInstallation: boolean = false;
  /** Flags que definem qual tabela de valores é exibida **/
  isPulseChart: boolean = false;
  isReadChart: boolean = false;
  //flag de consulta de gráfico
  inInitialState: boolean = true;
  inVoltageChart: boolean = false;
  inCurrentChart: boolean = false;
  inConsumptionChart: boolean = false;
  inAparentPowerChart: boolean = false;
  inPowerFactorChart: boolean = false;

  isGeneratingChart: boolean = false;

  public range: FormGroup = this.formbuilder.group({
    rangeDate: [null],
  });

  /** Armazena o range de datas entre data inicial e data final **/
  public datesInRange: Date[] = [];

  createChartBoxOpen() {
    this.createChartBox = !this.createChartBox;
  }

  isStep1Concluded(): void {
    if (!this.isThereInstallationListed) {
      this.step1IsConcluded = false;
      return;
    }
    this.step1IsConcluded = true;
  }

  isStep2Concluded(): void {
    if (!this.selectedInstallationForm.valid) {
      this.step2IsConcluded = false;
      this.isPulseInstallation = false;
      return;
    }
    this.step2IsConcluded = true;
    this.isPulseInstallation = this.isPulseEquipment(
      this.selectedInstallationForm.value?.equipmentType,
    );
    this.selectedCounterForm.setValue(1);
  }

  /**
   * Verifica se o tipo de equipamento é de pulso, independente de
   * como a palavra "Pulso" esteja escrita na referência do tipo.
   */
  private isPulseEquipment(equipmentType: string | null | undefined): boolean {
    return !!equipmentType?.match(/pulso/i);
  }

  /**
   * Verifica se o tipo de equipamento é do tipo READ.
   */
  private isReadEquipment(equipmentType: string | null | undefined): boolean {
    return !!equipmentType?.match(/read/i);
  }

  isStep3Concluded(): void {
    if (!this.range.valid) {
      this.step3IsConcluded = false;
      return;
    }
    this.step3IsConcluded = true;
  }

  searchInstallations() {
    this.isLoadingInstallationSearch = true;
    this.filteredInstallations = [];
    this.selectedInstallations = [];
    if (!this.installationReferenceForm.valid) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'Por favor, preencha todos os campos corretamente!',
      });
      this.isLoadingInstallationSearch = false;
      return;
    }

    this.subscriptions = this.reportService
      .getInstallationByReference(
        this.installationReferenceForm.value,
        this.company,
      )
      .subscribe({
        next: (installations: any) => {
          installations.data.installation.edges.forEach((installation: any) => {
            if (
              installation.node.reference.includes(
                this.installationReferenceForm.value,
              )
            ) {
              this.filteredInstallations.push(
                new Installation(
                  installation.node.id,
                  installation.node.reference,
                  installation.node.device
                    ? installation.node.device.macAddress
                    : '',
                  installation.node.device != undefined
                    ? installation.node.device.equipmentType.reference +
                    ' ' +
                    installation.node.device.equipmentType.major +
                    '.' +
                    installation.node.device.equipmentType.minor +
                    '.' +
                    installation.node.device.equipmentType.revision
                    : null,
                ),
              );
            }
          });
          if (this.filteredInstallations.length == 0) {
            this.isThereInstallationListed = false;
            this.step2IsConcluded = false;
            this.noInstallationWarning = true;
          } else {
            this.isThereInstallationListed = true;
            this.noInstallationWarning = false;
          }
          this.isStep1Concluded();
          this.isLoadingInstallationSearch = false;
        },
        error: (error: any) => {
          console.log('installationSearchResult: ', error.message);
          Swal.fire({
            icon: 'warning',
            title: 'Oops...',
            text: `${this.errorService.errorLib(error.message)}`,
          });
          this.isLoadingInstallationSearch = false;
        },
        complete: () => {
          console.log('complete');
        },
      });
  }

  enableGenerationButton(): boolean {
    if (
      this.step1IsConcluded &&
      this.step2IsConcluded &&
      this.step3IsConcluded
    ) {
      return true;
    }
    return false;
  }

  generateChart(): void {

    // Pega o tipo de equipamento filtrado para selecionar a requisição correta de consumo
    let equipmentType = this.selectedInstallationForm.value.equipmentType;

    this.eqTypeWithVersion = equipmentType;

    this.isPulseChart = this.isPulseEquipment(equipmentType);
    this.isReadChart = !this.isPulseChart && this.isReadEquipment(equipmentType);

    if (this.isPulseChart) {
      this.generatePulseConsumptionChart();
    } else if (this.isReadChart) {
      this.generateReadConsumptionChart();
    } else {
      this.generateLightingConsumptionChart();
    }
  }

  private generatePulseConsumptionChart(): void {
    this.isGeneratingChart = true;
    this.chartData = [];

    this.subscriptions = this.reportChartService
      .getPulseConsumptionData(
        tzConvertISOStartDate(this.range.value.rangeDate[0]),
        tzConvertISOEndDate(this.range.value.rangeDate[1]),
        this.selectedInstallationForm.value.id,
        this.company,
        this.selectedCounterForm.value,
      )
      .subscribe({
        next: (data: any) => {
          data.data.pulseConsumption.edges.forEach(({ node }: any) => {
            const dateTime = node.datetime
              ? DateTime.fromISO(node.datetime).toFormat('dd/MM/yyyy')
              : '';

            this.chartData.push(
              new ReportChartData(
                /*macAddress*/ '',
                /*dateTime*/ dateTime,
                /*voltage*/ '',
                /*current*/ '',
                /*activePower*/ null,
                /*apparentPower*/ '',
                /*power*/ '',
                /*consumption*/ node.consumption?.toFixed(5),
                /*powerFactor*/ '',
                /*diff*/ '24',
              ),
            );
          });

          this.isGeneratingChart = false;
          this.sortAndBindChartData();
          this.createPulseChart();
        },
        error: (error) => this.handleChartError(error),
      });
  }

  private generateReadConsumptionChart(): void {
    this.isGeneratingChart = true;
    this.chartData = [];

    this.subscriptions = this.reportChartService
      .getReadConsumptionData(
        tzConvertISOStartDate(this.range.value.rangeDate[0]),
        tzConvertISOEndDate(this.range.value.rangeDate[1]),
        this.selectedInstallationForm.value.id,
        this.company,
      )
      .subscribe({
        next: (data: any) => {
          data.data.readConsumption.edges.forEach(({ node }: any) => {
            const dateTime = node.datetime
              ? DateTime.fromISO(node.datetime).toFormat('dd/MM/yyyy')
              : '';

            this.chartData.push(
              new ReportChartData(
                /*macAddress*/ '',
                /*dateTime*/ dateTime,
                /*voltage*/ '',
                /*current*/ '',
                /*activePower*/ null,
                /*apparentPower*/ '',
                /*power*/ '',
                /*consumption*/(node.consumption / 1000).toFixed(5),
                /*powerFactor*/ '',
                /*diff*/ '24',
              ),
            );
          });

          this.isGeneratingChart = false;
          this.sortAndBindChartData();
          this.createReadChart();
        },
        error: (error) => this.handleChartError(error),
      });
  }

  // ─── LIGHTING ─────────────────────────────────────────────────────────────────

  private generateLightingConsumptionChart(): void {
    this.isGeneratingChart = true;
    this.chartData = [];

    this.subscriptions = this.reportChartService
      .getLightingConsumptionData(
        tzConvertISOStartDate(this.range.value.rangeDate[0]),
        tzConvertISOEndDate(this.range.value.rangeDate[1]),
        this.selectedInstallationForm.value.id,
        this.company,
      )
      .subscribe({
        next: (data: any) => {
          data.data.lightingConsumption.edges.forEach(({ node }: any) => {
            const voltage = node.operatingVoltage?.toFixed(0);
            const current = node.meterCurrentConsumption?.toFixed(0);
            const pf = node.powerFactor / 1000;

            this.chartData.push(
              new ReportChartData(
                /*macAddress*/ '',
                /*dateTime*/ node.datetime,
                /*voltage*/ voltage,
                /*current*/ current,
                /*activePower*/ null,
                /*apparentPower*/((voltage * current) / 1000).toFixed(2),
                /*power*/((voltage * current * pf) / 1000).toFixed(2),
                /*consumption*/ node.consumption
                  ?.toFixed(3)
                  .replace(/\.?0+$/, ''),
                /*powerFactor*/ pf.toFixed(3),
                /*diff*/(node.timestampDiff / 3600).toFixed(2),
              ),
            );
          });

          this.isGeneratingChart = false;

          // Converte os datetimes para timezone local antes de ordenar
          this.chartData.forEach((d: any) => {
            d.dateTime = tzConvertUTC2Local(d.dateTime);
          });

          this.sortAndBindChartData();
          this.createVoltageChart();
        },
        error: (error: any) => this.handleChartError(error),
      });
  }

  private sortAndBindChartData(): void {
    this.chartData.sort(
      (a, b) =>
        DateTime.fromISO(a.dateTime!).toMillis() -
        DateTime.fromISO(b.dateTime!).toMillis(),
    );
    this.dataSource = new MatTableDataSource<ReportChartData>(this.chartData);
  }

  private handleChartError(error: any): void {
    console.log(error);
    this.isGeneratingChart = false;
    Swal.fire({
      title: 'Erro!',
      text: 'Não foi possivel gerar o gráfico, tente novamente!',
      icon: 'error',
      confirmButtonText: 'Ok',
    });
  }

  createPulseChart(): void {
    let consumptionData: any[] = [];
    let dateTimeData: any[] = [];

    this.chartData.forEach((chartData: any) => {
      consumptionData.push(chartData.consumption);
      dateTimeData.push(chartData.dateTime);
    });

    /** Caso não haja dados de consumo **/
    if (this.chartData.length < 1) {
      /** Limpa a lista de datas **/
      this.datesInRange = [];

      const startDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[0]).replace(/"/g, ''),
      );

      const endDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[1]).replace(/"/g, ''),
      );

      /** Enquanto a data inicial for menor que a dara final **/
      while (startDate <= endDate) {
        /** Armazena a data em uma lista de ranges **/
        this.datesInRange.push(new Date(startDate));
        /** Seta a data inicial somando +1 **/
        startDate.setDate(startDate.getDate() + 1);
      }
      /** Limpa o gráfico **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe o gráfico com a linha horizontal **/
      this.chartVariable =
        this.reportChartService.generateEmptyPulseConsumptionChart(
          this.datesInRange,
        );
    } else {
      /** Caso existam dados de consumo **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe os dados de consumo conforme os dados existentes **/
      this.chartVariable = this.reportChartService.generatePulseChart(
        consumptionData,
        dateTimeData,
      );
    }

    this.inInitialState = false;

    Swal.fire({
      icon: 'success',
      title: 'Sucesso!',
      text: 'Gráfico gerado com sucesso!',
    });
  }

  createReadChart(): void {
    let consumptionData: any[] = [];
    let dateTimeData: any[] = [];

    this.chartData.forEach((chartData: any) => {
      consumptionData.push(chartData.consumption);
      dateTimeData.push(chartData.dateTime);
    });

    /** Caso não haja dados de consumo **/
    if (this.chartData.length < 1) {
      /** Limpa a lista de datas **/
      this.datesInRange = [];

      /** Contantes utilizadas para armazenar as data incial e final do formulário convertidas **/
      const startDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[0]).replace(/"/g, ''),
      );

      const endDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[1]).replace(/"/g, ''),
      );

      /** Enquanto a data inicial for menor que a dara final **/
      while (startDate <= endDate) {
        /** Armazena a data em uma lista de ranges **/
        this.datesInRange.push(new Date(startDate));
        /** Seta a data inicial somando +1 **/
        startDate.setDate(startDate.getDate() + 1);
      }
      /** Limpa o gráfico **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe o gráfico com a linha horizontal **/
      this.chartVariable =
        this.reportChartService.generateEmptyReadConsumptionChart(
          this.datesInRange,
        );
    } else {
      /** Caso existam dados de consumo **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe os dados de consumo conforme os dados existentes **/
      this.chartVariable = this.reportChartService.generateReadChart(
        consumptionData,
        dateTimeData,
      );
    }

    this.inInitialState = false;

    Swal.fire({
      icon: 'success',
      title: 'Sucesso!',
      text: 'Gráfico gerado com sucesso!',
    });
  }

  createVoltageChart(): void {
    let voltageData: any[] = [];
    let currentData: any[] = [];
    let apparentPowerData: any[] = [];
    let powerData: any[] = [];
    let consumptionData: any[] = [];
    let powerFactorData: any[] = [];
    let dateTimeData: any[] = [];

    this.chartData.forEach((chartData: any) => {
      voltageData.push(chartData.voltage);
      currentData.push(chartData.current);
      apparentPowerData.push(chartData.apparentPower);
      powerData.push(chartData.power);
      consumptionData.push(chartData.consumption);
      powerFactorData.push(chartData.powerFactor);
      dateTimeData.push(chartData.dateTime);
    });

    /** Caso não haja dados de consumo **/
    if (this.chartData.length < 1) {
      /** Limpa a lista de datas **/
      this.datesInRange = [];

      /** Contantes utilizadas para armazenar as data incial e final do formulário convertidas **/
      const startDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[0]).replace(/"/g, ''),
      );

      const endDate = new Date(
        JSON.stringify(this.range?.value?.rangeDate[1]).replace(/"/g, ''),
      );

      /** Enquanto a data inicial for menor que a dara final **/
      while (startDate <= endDate) {
        /** Armazena a data em uma lista de ranges **/
        this.datesInRange.push(new Date(startDate));
        /** Seta a data inicial somando +1 **/
        startDate.setDate(startDate.getDate() + 1);
      }
      /** Limpa o gráfico **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe o gráfico com a linha horizontal **/
      this.chartVariable =
        this.reportChartService.generateEmptyConsumptionChart(
          this.datesInRange,
        );
    } else {
      /** Caso existam dados de consumo **/
      this.chartVariable ? this.chartVariable.destroy() : null;

      /** Exibe os dados de consumo conforme os dados existentes **/
      this.chartVariable = this.reportChartService.generateChart(
        voltageData,
        currentData,
        apparentPowerData,
        powerData,
        consumptionData,
        dateTimeData,
        powerFactorData,
      );
    }

    this.inInitialState = false;

    Swal.fire({
      icon: 'success',
      title: 'Sucesso!',
      text: 'Gráfico gerado com sucesso!',
    });
  }

  resetForm(): void {
    //limpar o formulário
    this.range.reset();
    this.selectedInstallationForm.reset();
    this.installationReferenceForm.reset();
    this.selectedCounterForm.setValue(1);
    this.isPulseInstallation = false;
    this.filteredInstallations = [];
    this.selectedInstallations = [];
    this.step1IsConcluded = false;
    this.step2IsConcluded = false;
    this.step3IsConcluded = false;
    this.isThereInstallationListed = false;
    this.noInstallationWarning = false;
    this.isGeneratingChart = false;
  }

  /**
   * Retorna os cabecalhos, chaves e titulo com base no tipo de equipamento
   * para a  exportacao de documento em csv ou pdf.
   */
  private getMetadata(): { headers: string[]; keys: string[]; title: string } {
    let headers: string[] = [];
    let keys: string[] = [];
    let title: string = 'Relatorio_Dados_Grafico';

    //referencia da instalacao selecionada
    let referenceId = this.filteredInstallations.filter(
      (installation) => installation.id === this.selectedInstallationForm.value,
    )[0]?.reference;

    let equipmentType = this.selectedInstallationForm.value.equipmentType;

    if (this.isPulseEquipment(equipmentType)) {
      headers = ['Data/Hora', 'Consumo (PULSO)'];
      keys = ['dateTime', 'consumption'];
      title = `Relatorio_Consumo_PULSO_${referenceId}`;
    } else if (this.isReadEquipment(equipmentType)) {
      headers = ['Data/Hora', 'Consumo (READ)'];
      keys = ['dateTime', 'consumption'];
      title = `Relatorio_Consumo_READ_${referenceId}`;
    } else {
      headers = [
        'Data/Hora',
        'Tensão',
        'Corrente(mA)',
        'Potência Aparente(vA)',
        'Potência (W)',
        'Consumo(kWh)',
        'Fator de Potência',
      ];
      keys = [
        'dateTime',
        'voltage',
        'current',
        'apparentPower',
        'power',
        'consumption',
        'powerFactor',
      ];
      title = `Relatorio_Dados_Medicao_${referenceId}`;
    }

    return { headers, keys, title };
  }

  /**
   * exporta os dados da tabela para  o formato CSV.
   */
  downloadChartCsv(): void {
    if (this.chartData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sem Dados',
        text: 'Não há dados para exportar.',
      });

      return;
    }

    const { headers, keys, title } = this.getMetadata();

    const reportTitle = 'Relatório de Medição';

    const requester =
      localStorage.getItem('username') ?? 'Não informado';

    const emissionDate =
      DateTime.local().toFormat('dd/MM/yyyy');

    let csvData = '';

    csvData += `Emissão;${emissionDate}\n`;
    csvData += `Relatório;${reportTitle}\n`;
    csvData += `Solicitante;${requester}\n`;

    csvData += '\n';

    csvData += headers.join(';') + '\n';

    // Dados da tabela
    this.chartData.forEach((row) => {
      const rowData = keys.map((key) => {
        let value = row[key as keyof ReportChartData] ?? '';

        if (
          typeof value === 'number' ||
          (
            typeof value === 'string' &&
            value.match(/^-?\d+(\.\d+)?$/)
          )
        ) {
          value = String(value).replace('.', ',');
        }

        value = String(value).replace(/"/g, '""');

        if (value.includes(';') || value.includes('\n')) {
          value = `"${value}"`;
        }

        return value;
      });

      csvData += rowData.join(';') + '\n';
    });

    const blob = new Blob(
      ['\uFEFF' + csvData],
      {
        type: 'text/csv;charset=utf-8;',
      }
    );

    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.setAttribute('href', url);

    const today = DateTime.local().toFormat('yyyyMMdd');

    link.setAttribute(
      'download',
      `${title}_${today}.csv`
    );

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  }

  /**
   * exporta os dados da tabela  em formato em PDF.
   */
  public downloadChartPDF(): void {
    if (this.chartData.length === 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Sem Dados',
        text: 'Não há dados para exportar.',
      });

      return;
    }

    const { headers, keys, title } = this.getMetadata();

    const reportTitle = 'Relatório de Medição';

    const requester =
      localStorage.getItem('username') ?? 'Não informado';

    const bodyData = this.chartData.map((row) => {
      return keys.map((key) => {
        const value = row[key as keyof ReportChartData] ?? 'N/A';

        return String(value).replace('.', ',');
      });
    });

    const doc: any = new jsPDF('l', 'mm', 'a4');

    const emissionDate =
      DateTime.local().toFormat('dd/MM/yyyy');

    const pageWidth =
      doc.internal.pageSize.getWidth();

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);

    doc.text(
      `Emissão ${emissionDate}`,
      15,
      15
    );

    doc.text(
      reportTitle,
      pageWidth / 2,
      15,
      {
        align: 'center',
      }
    );

    doc.text(
      `Solicitante: ${requester}`,
      pageWidth - 15,
      15,
      {
        align: 'right',
      }
    );

    // Linha separadora
    doc.setDrawColor(180, 180, 180);
    doc.line(
      15,
      18,
      pageWidth - 15,
      18
    );

    (doc as any).autoTable({
      head: [headers],
      body: bodyData,

      theme: 'grid',

      margin: {
        top: 22,
        left: 15,
        right: 15,
      },

      styles: {
        fontSize: 9,
        valign: 'middle',
      },

      headStyles: {
        fillColor: [45, 155, 140],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
      },
    });

    const today =
      DateTime.local().toFormat('yyyyMMdd');

    const fileName = `${title}_${today}.pdf`;

    doc.save(fileName);
  }
}
